import { LitElement, html } from 'lit';
import typographyStyles from '@cof/fs-typography-core/lib/typography.lit';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { fs24Tag } from '@cof/fs-icons';
import { searchIcon } from './assets/search-icon.js';
import '@cof/fs-container-wc/fs-container.js';
import '@cof/fs-typography-wc/fs-heading.js';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { DebugService } from '../../services/debug/debug.service.js';
import { style } from './codeless-entry.component.style.js';
import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
import '../offer/header-message/offer-header-message.component.js';
import SessionService from '../../services/session/session.service.js';
import { CustomerOffersService } from '../../services/customer-offers/customer-offers.service.js';
import { MatchingState } from '../../models/search-customer-offers.model.js';
import { stratum } from '../../constants/stratum.js';
import { StorageService } from '../../services/storage/storage.service.js';
import { HostAppEventsService } from '../../utils/host-app-events-service.js';
import { setCodelessEntryFlowViewed } from '../../services/codeless-entry/codeless-entry.service.js';
import { CODELESS_ENTRY_CONST } from '../../constants/constants.js';

class RefiCodelessEntry extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiCodelessEntryComponent');
    this.hasApps = false;
    this.loadingPage = true;
    this.loadingAfterContinueClicked = false;
    this.promotionalCode = '';
    this.grOfferInvalid = false;
    this.showErrorPageFirstTime = true;
    this.showErrorPage = false;
    this.noExternalId = false;
    this.apr = 0;
    this.expirationDate = '';
    FsIcon.add([fs24Tag]);
  }

  static styles = [style, typographyStyles];

  static properties = {
    hasApps: { type: Boolean, attribute: 'has-apps', reflect: true },
    loadingPage: { type: Boolean, state: true },
    loadingAfterContinueClicked: { type: Boolean },
    showErrorPage: { type: Boolean, state: true },
    grOfferInvalid: { type: Boolean, state: true },
    showErrorPageFirstTime: { type: Boolean, state: true },
    redirecting: { type: Boolean, state: true },
    noExternalId: { type: Boolean, state: true },
    apr: { type: Number, state: true },
    expirationDate: { type: String, state: true },
    promotionalCode: { type: String, state: true },
  };

  async connectedCallback() {
    super.connectedCallback();
    document.dispatchEvent(new CustomEvent('show-progress-bar', { detail: { displayBar: false } }));
    // Parse the external_id to get the APR, promotional code, and expiration date
    this.parseExternalId().then(() => {
      // Hide the loading spinner and show the appropriate page (splash or error) when complete
      this.loadingPage = false;
    });
  }

  async parseExternalId() {
    // Get external_id from the SessionStorage and break it into parts (last 3 should be apr, promo code, and expiration date)
    const externalId = SessionService.getExternalId();
    const parts = externalId ? externalId.split('_') : null;

    // If external_id is valid, get the promotional code and check if it matches the promotional code in the customer offers
    if (parts && parts.length >= 3) {
      this.noExternalId = false;
      // Promo code in the external_id
      this.promotionalCode = parts[parts.length - 2];

      stratum.publishTag(TagKey.CODELESS_ENTRY_OFFERS_CALL_ATTEMPTED);

      const requestBody = this.promotionalCode ? { promotionalCode: this.promotionalCode } : {};

      // Get the customer offers with the call to the refi-host-app-bff
      const offerDetails = await CustomerOffersService.getCustomerOffers(requestBody, MatchingState.primarySso, true);

      // Check if we got a valid response from the API call
      if (offerDetails !== null) {
        // Check if the promotional code in the customer offers matches the one in the SessionStorage
        if (offerDetails.promotionalCode === this.promotionalCode) {
          stratum.publishTag(TagKey.CODELESS_ENTRY_OFFER_APPLIED);

          // All the criteria for GREASE splash page are met, so the page can be shown
          this.showErrorPage = false;

          // Set the APR that the page will display
          this.apr = this.getAprFromParts(parts);

          // Set the expiration date that the page will display
          this.expirationDate = this.generateExpiryDate(parts[parts.length - 1]);

          // Save the offer details to the session storage so that it's available for the application
          SessionService.setCachedOffer(offerDetails);
          StorageService.setShowOfferBannerInSession(false);

          // v2/PAA or v2/Personal
          this.redirecting = true;
          this.continueToNextStep();
        } else {
          // Promotional code is expired or does not match, so take them to the landing page instead
          this.showErrorPage = true;
          this.grOfferInvalid = true;
          stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_CODELESS_ENTRY);

          if (offerDetails.isExpired) {
            stratum.publishTag(TagKey.CODELESS_ENTRY_NO_VALID_OFFERS);
          } else {
            stratum.publishTag(TagKey.CODELESS_ENTRY_PROMOCODE_MISMATCH);
          }
        }
      } else {
        // No proper response from the CO API (technical difficulty)
        this.showErrorPage = true;
        stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_CODELESS_ENTRY);
      }
    } else {
      // No external_id in SessionStorage
      this.showErrorPage = true;
      this.noExternalId = true;
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_CODELESS_ENTRY);

      if (!externalId) {
        stratum.publishTag(TagKey.CODELESS_ENTRY_NO_EXTERNAL_ID);
      } else {
        stratum.publishTag(TagKey.CODELESS_ENTRY_INVALID_EXTERNAL_ID);
      }
    }

    this.updateErrorPageShownCount();
  }

  updateErrorPageShownCount() {
    if (this.showErrorPage) {
      this.redirecting = false;
      setCodelessEntryFlowViewed(true);

      if (!this.grOfferInvalid) {
        const count = Number(SessionService.getShowErrorMessageCount());

        if (!count) {
          SessionService.setShowErrorMessageCount(1);
          stratum.publishTag(TagKey.CODELESS_ENTRY_OFFERS_CALL_FAILED);
        } else {
          SessionService.setShowErrorMessageCount(count + 1);
          stratum.publishTag(TagKey.CODELESS_ENTRY_OFFERS_CALL_FAIL_EXCEEDED);
        }

        const newCount = SessionService.getShowErrorMessageCount();
        this.showErrorPageFirstTime = newCount === null || Number(newCount) <= 1;
      } else {
        this.debug.log(`updateErrorPageShownCount: Skipping error count update because grOfferInvalid = true`);
      }
    }
  }

  getAprFromParts(parts) {
    const apr = parts[parts.length - 3];
    return Number(apr && apr.indexOf('d') > -1 ? apr.replace('d', '.') : apr);
  }

  // Method to convert a date in YYYYMMDD format to a human-readable date (Month DD, YYYY)
  generateExpiryDate(expirationDate) {
    const year = expirationDate.slice(0, 4);
    const month = expirationDate.slice(4, 6);
    const day = expirationDate.slice(6, 8);
    const parsedDate = new Date(year, parseInt(month, 10) - 1, day);
    return parsedDate?.toLocaleDateString('en-us', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  continueButtonClicked() {
    stratum.publishTag(TagKey.CODELESS_ENTRY_CONTINUE_WITHOUT_OFFER_CLICKED);
    this.continueToNextStep();
  }

  continueToNextStep() {
    setCodelessEntryFlowViewed(true);
    // Make the continue button show the loading spinner on it
    this.loadingAfterContinueClicked = true;
    document.dispatchEvent(new CustomEvent('sign-out-toggle', { detail: { showSignOut: true } }));
    // If the user has apps, go to the pick-an-app page, otherwise go to the new application page
    if (this.hasApps) {
      const params = new URLSearchParams(window.location.search);
      this.debug.log('dispatching next-step-event');
      HostAppEventsService.sendNextStepEvent({ step: 'pick-an-app', data: params.toString() ? params.toString() : '' });
    } else {
      HostAppEventsService.sendStartNewAppEvent();
    }
  }

  // Backend call to match the offer with tokenized ssn when "Try Again" CTA is clicked
  tryAgainClicked() {
    stratum.publishTag(TagKey.CODELESS_ENTRY_TRY_AGAIN_CLICKED);

    this.loadingPage = true;
    this.parseExternalId().then(() => {
      // Hide the loading spinner and show the appropriate page (splash or error) when complete
      this.loadingPage = false;
    });
  }

  renderLoadingPage() {
    return html` <refi-loading-spinner class="_gb-show"></refi-loading-spinner> `;
  }

  renderHeaderMessage(heading, message) {
    return html`<offer-header-message heading="${heading}" message="${message}"></offer-header-message>`;
  }

  renderErrorPageFailExceededOrNoExternalId() {
    return html`<div class="error-container">
      <div class="header-body-container">${this.renderHeaderMessage(CODELESS_ENTRY_CONST.TECHNICAL_DIFFICULTIES_HEADING, CODELESS_ENTRY_CONST.TECHNICAL_DIFFICULTIES_MESSAGE_LATER)}</div>
    </div> `;
  }

  renderErrorPageFailFirstTime() {
    return html`<div class="error-container">
      <div class="header-body-container">${this.renderHeaderMessage(CODELESS_ENTRY_CONST.TECHNICAL_DIFFICULTIES_HEADING, CODELESS_ENTRY_CONST.TECHNICAL_DIFFICULTIES_MESSAGE_RETRY)}</div>
      <div class="button-container">
        <fs-button variant="primary" type="button" class="continue-button" ?loading="${this.loadingAfterContinueClicked}" @click="${this.tryAgainClicked}">${CODELESS_ENTRY_CONST.TRY_AGAIN_CTA}</fs-button>
      </div>
    </div>`;
  }

  renderErrorPageNoOfferMatch() {
    return html`<div class="error-container">
      <div class="header-body-container">
        ${searchIcon}
        <div class="message-container" role="alert">
          <div class="body-content" data-e2e-ref="codelessEntryBody">
            <fs-heading class="title-text" level="1" size="large" data-e2e-ref="codelessEntryHeading">${CODELESS_ENTRY_CONST.NO_OFFER_MATCH_HEADING}</fs-heading>
            <div class="reasons-section">
              <p>${CODELESS_ENTRY_CONST.NO_OFFER_MATCH_REASONS_INTRO}</p>
              <ul class="reason-list">
                <li>${CODELESS_ENTRY_CONST.OFFER_EXPIRED_REASON}</li>
                <li>${CODELESS_ENTRY_CONST.OFFER_DIFFERENT_ACCOUNT_REASON}</li>
              </ul>
            </div>
            <p>${CODELESS_ENTRY_CONST.NO_OFFER_MATCH_MESSAGE}</p>
          </div>
        </div>
      </div>
      <div class="button-container">
        <fs-button variant="primary" type="button" class="continue-button" ?loading="${this.loadingAfterContinueClicked}" @click="${this.continueButtonClicked}"
          >${this.hasApps ? CODELESS_ENTRY_CONST.SEE_OTHER_APPLICATIONS_CTA : CODELESS_ENTRY_CONST.START_NEW_APPLICATION_CTA}</fs-button
        >
      </div>
    </div>`;
  }

  render() {
    return html` ${this.redirecting
      ? this.renderLoadingPage()
      : html` ${this.loadingPage
          ? this.renderLoadingPage()
          : html` ${this.showErrorPage
              ? html` ${this.grOfferInvalid
                  ? this.renderErrorPageNoOfferMatch()
                  : html` ${this.noExternalId ? this.renderErrorPageFailExceededOrNoExternalId() : this.showErrorPageFirstTime ? this.renderErrorPageFailFirstTime() : this.renderErrorPageFailExceededOrNoExternalId()}`}`
              : html``}`}`}`;
  }
}

customElements.define('refi-codeless-entry', RefiCodelessEntry);
